import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button,
  IconButton,
  Skeleton,
} from '@mui/material';
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Visitor } from "../../../../assets/images/visitor.svg";
import { ReactComponent as Person } from "../../../../assets/images/person.svg";
import { ReactComponent as AccountTypeIcon } from '../../../../assets/images/accountlogo.svg';
import { ReactComponent as Source } from '../../../../assets/images/source.svg';
import { ReactComponent as Status } from '../../../../assets/images/status.svg';
import { ReactComponent as Assign } from '../../../../assets/images/assign.svg';

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

const FeedbackQueueSidebar = ({ isOpen, selectedItem, onClose, onAssignClick, handleComplete, handleDelete }) => {
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const navigate = useNavigate();
  const userData = useSelector((state) => state.loginToken);
  const kbRole = userData?.response?.user?.kbRole?.role
  useEffect(() => {
    if (!isOpen) {
      setIsAssignOpen(false);
    }
  }, [isOpen]);
  // Dynamic data from selectedItem (API response or row data)
  const feedbackData = useMemo(() => {
    if (!selectedItem) return null;
    return {
      article: selectedItem.article || selectedItem.fullData?.articleId?.title || 'N/A',
      name: selectedItem.fullData?.assigneeId?.full_name || 'Unassigned',
      source: selectedItem.source ? selectedItem.source.charAt(0).toUpperCase() + selectedItem.source.slice(1) : 'Internal',
      status: selectedItem.status === 'inProgress' || selectedItem.status === 'InProgress' ? 'In Progress' : (selectedItem.status ? selectedItem.status.charAt(0).toUpperCase() + selectedItem.status.slice(1) : 'New'),
      comments: selectedItem.fullData?.comments || [],
    };
  }, [selectedItem]);



  const renderSkeleton = () => (
    <Box sx={{ padding: '32px' }}>
      {/* Header Skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '40px' }}>
        <Skeleton variant="text" width="150px" height={32} />
        <Skeleton variant="circular" width={24} height={24} />
      </Box>

      {/* Article Skeleton */}
      <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="80%" height={18} />

      {/* Name, Source, Status Skeletons */}
      <Box sx={{ mt: '32px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '123px', mb: '24px' }}>
          <Skeleton variant="text" width="60px" height={18} />
          <Skeleton variant="text" width="120px" height={18} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '112px', mb: '24px' }}>
          <Skeleton variant="text" width="60px" height={18} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '119px', mb: '40px' }}>
          <Skeleton variant="text" width="60px" height={18} />
          <Skeleton variant="rounded" width={70} height={24} />
        </Box>

        {/* Comment Skeleton */}
        <Box sx={{ border: '1px solid #E5E9EC', borderRadius: '8px' }}>
          <Skeleton variant="text" width="200px" height={64} sx={{ borderBottom: '1px solid #E5E9EC' }} />
          <Skeleton variant="text" width="100%" height={28} sx={{ px: 3, py: 3 }} />
          <Skeleton variant="text" width="90%" height={28} sx={{ px: 3 }} />
        </Box>
      </Box>
    </Box>
  );

  if (!isOpen) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100vh',
          width: isOpen ? '552px' : '0',
          transition: 'width 0.3s ease-in-out',
          zIndex: 1000,
          backgroundColor: 'background.paper',
          boxShadow: isOpen ? '0 4px 80px 0 rgba(6, 21, 34, 0.10)' : 'none',
          overflowY: 'auto',
        }}
      >
        {!feedbackData ? (
          renderSkeleton()
        ) : (
          <>
            <Box sx={{ padding: '32px' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '40px' }}>
                <Typography
                  sx={{
                    color: '#061522',
                    fontFamily: 'Inter',
                    fontSize: '24px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}
                >
                  Feedback Details
                </Typography>
                <IconButton onClick={onClose} sx={{ p: 0.5 }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Article Title */}
              <Typography
                sx={{
                  color: '#405261',
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '18px'
                }}
              >
                Article
              </Typography>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    color: '#061522',
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    width: '300px',
                    lineHeight: '18px',
                    marginTop: '8px'
                  }}
                >
                  {feedbackData.article}
                </Typography>
                <Button
                  variant="outlined"
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    borderRadius: '8px',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderColor: '#D5DCE1',
                    color: '#405261',
                    '&:hover': { borderColor: '#C5CED6', backgroundColor: '#FFFFFF' },
                    height: '40px',
                    width: '128px',
                  }}

                  onClick={() => {
                    const articleId = selectedItem?.fullData?.articleId?._id;

                    if (!articleId) {
                      toast.error("Article not found");
                      handleMenuClose();
                      return;
                    }

                    navigate(`/kb/article/${articleId}`);
                  }}

                >
                  Edit Article
                </Button>
              </div>

              {/* Name */}
              <Box sx={{ mb: '24px', marginTop: '32px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '123px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AccountTypeIcon />
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '18px'
                      }}
                    >
                      Name
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: '#061522',
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '18px'
                    }}
                  >
                    {feedbackData.name}
                  </Typography>
                </Box>
              </Box>

              {/* Source */}
              <Box sx={{ mb: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '112px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Source width={24} height={24} />
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '18px'
                      }}
                    >
                      Source
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: 'flex', width: '80px',
                    justifyContent: 'center',
                    gap: 0.5,
                    height: '24px', alignItems: 'center', borderRadius: '5px', border: '1px solid #D5DCE1', background: '#FFF'
                  }}>
                    {feedbackData.source === 'Visitor' ? <Visitor /> : <Person />}
                    <Typography sx={{ color: '#405261', fontSize: 12, fontWeight: 400 }}>
                      {feedbackData.source}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Status */}
              <Box sx={{ mb: '40px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '119px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Status width={24} height={24} />
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '18px'
                      }}
                    >
                      Status
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 24,
                      width: feedbackData.status === 'New' ? '48px' : '88px',
                      borderRadius: '5px',
                      border: feedbackData.status === 'Completed' ? '1px solid #148DFD' : feedbackData.status === 'New' ? '1px solid #52C41A' : '1px solid #FA8C16',
                      background: feedbackData.status === 'Completed' ? '#F3FBFF' : feedbackData.status === 'In Progress' ? '#FFF7E6' : '#F6FFED',
                      color: '#405261',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                      {feedbackData.status}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Feedback Comment Section */}
              <Box
                sx={{
                  border: '1px solid #E5E9EC',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    color: '#061522',
                    fontFamily: 'Inter',
                    fontSize: '18px',
                    height: '64px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    paddingTop: '22px',
                    paddingLeft: '24px',
                    borderBottom: '1px solid #E5E9EC',
                  }}
                >
                  {feedbackData.comments.length > 1 ? `Feedback Comments` : `Feedback Comment`}
                </Typography>
                <Box sx={{ padding: '24px' }}>
                  {feedbackData.comments.length > 0 ? (
                    feedbackData.comments.map((comment, index) => (
                      <Box key={comment._id || index}>
                        <Typography
                          sx={{
                            color: '#405261',
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '28px',
                          }}
                        >
                          {comment.comment}
                        </Typography>
                        {index !== feedbackData.comments.length - 1 && (
                          <Box
                            sx={{
                              height: '1px',
                              backgroundColor: '#E5E9EC',
                              my: '16px',
                            }}
                          />
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '28px',
                      }}
                    >
                      No comment available
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: '24px',
                left: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                width: '504px',
                right: '24px'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Assign />}
                sx={{
                  borderRadius: '8px',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderColor: '#D5DCE1',
                  color: '#405261',
                  minWidth: '144px',
                  '&:hover': { borderColor: '#C5CED6', backgroundColor: '#FFFFFF' },
                  height: '40px',
                }}
                onClick={onAssignClick}
              >
                Assign to
              </Button>
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                }}
              >
                {kbRole === 'editor' && <Button
                  variant="outlined"
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    borderRadius: '8px',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderColor: '#D5DCE1',
                    color: '#FF4D4F',
                    '&:hover': { borderColor: '#C5CED6', backgroundColor: '#FFFFFF' },
                    height: '40px',
                    width: '96px',
                  }}

                  onClick={() => {
                    handleDelete();
                  }}

                >
                  Delete
                </Button>
                }
                <Button
                  variant="contained"
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    borderRadius: '8px',
                    width: '128px',
                    fontWeight: 500,
                    textTransform: 'none',
                    backgroundColor: '#148DFD',
                    color: 'white',
                    '&:hover': { backgroundColor: '#0077CC' },
                    height: '40px',
                  }}
                  onClick={() => handleComplete()}
                >
                  Complete
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
      {/* <AssignFeedbackQueue
        isOpen={isAssignOpen}
        onClose={() => { setIsAssignOpen(false) }}
        selectedItem={selectedItem}
        onAssignSuccess={handleAssignSuccess}
      /> */}
    </>
  );
};

export default FeedbackQueueSidebar;